import React from "react";
import BottomSheet from "../../components/mobile/bottom-sheet";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import type { MovementActivity } from "../../lib/api";
import {
  getDefaultExtensionDateFrom,
  getMovementDaysHired,
  inclusiveDaysBetween,
} from "./extend-days";

interface ExtendDaysHiredSheetProps {
  open: boolean;
  onClose: () => void;
  selectedMovements: MovementActivity[];
  dateTo: string;
  onDateToChange: (value: string) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  minDate?: string;
  isInvalid: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
}

const ExtendDaysHiredSheet: React.FC<ExtendDaysHiredSheetProps> = ({
  open,
  onClose,
  selectedMovements,
  dateTo,
  onDateToChange,
  notes,
  onNotesChange,
  minDate,
  isInvalid,
  isSubmitting,
  onSubmit,
}) => {
  const count = selectedMovements.length;

  return (
    <BottomSheet
      open={open}
      onClose={() => {
        if (!isSubmitting) onClose();
      }}
      title="Extend days hired"
      description={`${count} movement${count === 1 ? "" : "s"} selected. Date from is calculated per movement from its hire period.`}
      footer={
        <button
          type="button"
          disabled={isSubmitting || isInvalid}
          onClick={onSubmit}
          className="h-12 w-full rounded-2xl border-0 bg-primary-600 text-sm font-semibold text-white active:bg-primary-700 disabled:opacity-40"
        >
          {isSubmitting
            ? "Extending..."
            : `Extend ${count} movement${count === 1 ? "" : "s"}`}
        </button>
      }
    >
      <div className="space-y-5 px-5 pb-4">
        <div className="space-y-1.5">
          <Label
            htmlFor="mobile-bulk-extend-date-to"
            className="text-sm text-gray-600"
          >
            Date To
          </Label>
          <Input
            id="mobile-bulk-extend-date-to"
            type="date"
            className="h-11 w-full text-[15px]"
            value={dateTo}
            min={minDate}
            disabled={isSubmitting}
            onChange={(event) => onDateToChange(event.target.value)}
          />
          <p className="text-xs text-gray-500">
            Hire start, or the day after the current hire end.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="mobile-bulk-extend-notes"
            className="text-sm text-gray-600"
          >
            Notes (Optional)
          </Label>
          <Input
            id="mobile-bulk-extend-notes"
            className="h-11 w-full text-[15px]"
            value={notes}
            disabled={isSubmitting}
            onChange={(event) => onNotesChange(event.target.value)}
            placeholder="Reason for extension"
          />
        </div>

        {dateTo && count > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Preview
            </p>
            <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-100">
              {selectedMovements.map((movement) => {
                const dateFrom = getDefaultExtensionDateFrom(movement);
                const calculatedDays = inclusiveDaysBetween(dateFrom, dateTo);
                const currentDays = getMovementDaysHired(movement);
                return (
                  <div
                    key={movement.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {movement.reference?.trim() || movement.id.slice(0, 8)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {movement.activitySubType}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p
                        className={
                          calculatedDays == null
                            ? "text-sm font-semibold text-red-600"
                            : "text-sm font-semibold text-gray-900"
                        }
                      >
                        +{calculatedDays ?? "—"} day
                        {calculatedDays === 1 ? "" : "s"}
                      </p>
                      {calculatedDays != null &&
                        Number.isFinite(currentDays) && (
                          <p className="text-xs text-gray-500">
                            New total {currentDays + calculatedDays}
                          </p>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </BottomSheet>
  );
};

export default ExtendDaysHiredSheet;
